import { Download, Eye, EyeOff, Headphones, Minus, Plus, VolumeX } from "lucide-react";
import { useMemo, useState } from "react";
import { Brand } from "@/components/Brand.tsx";
import { StatusPill } from "@/components/StatusPill.tsx";
import { TranscriptView } from "@/components/TranscriptView.tsx";
import type { Phase } from "@/hooks/useBooth.ts";
import { useEngineInfo } from "@/hooks/useEngineInfo.ts";
import { useListener } from "@/hooks/useListener.ts";
import { recordingUrl, useRecordings, type RecordingInfo } from "@/hooks/useRecordings.ts";
import { languageName } from "@/lib/languages.ts";
import { inLanguage, segments as orderedSegments } from "@/lib/transcript.ts";

export function ListenerPage({ code }: { code: string }) {
  const listener = useListener(code);
  const { transcript, link, session, error, lang } = listener;
  const { languages } = useEngineInfo(60000);
  const [showSource, setShowSource] = useState(false);
  const [scale, setScale] = useState(1.15);
  const track = listener.track;
  const segments = useMemo(
    () => orderedSegments(transcript, track).map((s) => (track === "main" ? inLanguage(s, lang) : s)),
    [transcript, lang, track],
  );

  const offered = session?.listenerLanguages ?? [];
  const translated = session?.attendeeLanguages ?? offered;
  const isOriginal = (code: string) => !translated.includes(code);
  const voiced = Boolean(lang && session?.voices && lang in session.voices && !isOriginal(lang));
  const ended = transcript.state === "stopped";
  const phase: Phase = error
    ? "error"
    : link === "reconnecting"
      ? "reconnecting"
      : link === "connecting"
        ? "connecting"
        : ended || link === "closed"
          ? "stopped"
          : "live";
  const label = ended ? "Session ended" : phase === "live" ? (transcript.state === "paused" ? "Speaker reconnecting" : "Following") : undefined;
  const langLabel = lang ? languageName(languages, lang) : "";
  const { recordings, expiresInS } = useRecordings(code, ended);

  return (
    <div className="mx-auto flex h-dvh w-full max-w-3xl flex-col gap-3 px-4 py-4">
      <header className="flex items-center justify-between gap-3">
        <Brand subtitle={`Following ${code.toUpperCase()}`} />
        <StatusPill phase={phase} label={label} />
      </header>
      {session?.simulated ? (
        <p className="rounded-xl border border-accent bg-accent-soft px-4 py-2 text-sm">The speaker is running the scripted demo.</p>
      ) : null}
      {error ? (
        <p role="alert" className="rounded-xl border border-danger px-4 py-2 text-sm">
          {error}
        </p>
      ) : null}

      {offered.length > 0 ? (
        <section className="card flex flex-col gap-3 p-3 sm:flex-row sm:items-center" aria-label="Your language">
          <label className="flex min-w-0 flex-1 items-center gap-2 text-sm text-muted">
            <span className="shrink-0">My language</span>
            <select
              className="field min-w-0 flex-1 text-base text-fg"
              value={lang ?? ""}
              onChange={(e) => listener.setLanguage(e.target.value)}
            >
              {[...translated.filter((c) => offered.includes(c)), ...offered.filter(isOriginal)].map((code_) => (
                <option key={code_} value={code_}>
                  {languageName(languages, code_)}
                  {isOriginal(code_)
                    ? " (original, text only)"
                    : session?.voices && code_ in session.voices
                      ? ""
                      : " (text only)"}
                </option>
              ))}
            </select>
          </label>
          {voiced && !ended ? (
            listener.audioOn ? (
              <button type="button" className="btn min-h-12 border-danger px-5" onClick={() => void listener.stopAudio()}>
                <VolumeX size={18} /> Stop listening
              </button>
            ) : (
              <button
                type="button"
                className="btn min-h-12 border-accent bg-accent px-5 text-base font-medium text-accent-ink"
                onClick={() => void listener.startAudio()}
              >
                <Headphones size={18} /> Listen in {langLabel}
              </button>
            )
          ) : null}
        </section>
      ) : null}
      {ended && Object.keys(recordings).length ? (
        <DownloadCard code={code} lang={lang} recordings={recordings} expiresInS={expiresInS} name={(c) => languageName(languages, c)} />
      ) : null}
      {listener.audioOn && !ended ? (
        <p className="text-xs text-muted" aria-live="polite">
          {listener.backlog > listener.delayS + 6
            ? "Catching up with the speaker…"
            : listener.realtime
              ? "Keep this page open; the screen stays on while you listen. Headphones recommended. Speech plays live, as the speaker talks."
              : `Keep this page open; the screen stays on while you listen. Headphones recommended. Speech plays ${listener.delayS.toFixed(1)} s behind the text so it flows without gaps.`}
        </p>
      ) : null}

      <main className="card flex min-h-0 flex-1 flex-col overflow-hidden">
        <TranscriptView
          segments={segments}
          languages={languages}
          direction={session?.plan.direction ?? "one-way"}
          speakerA={session?.plan.source ?? ""}
          layout="interleaved"
          showSource={showSource}
          textScale={scale}
          live={phase === "live"}
          empty={error ? "This listening code is not active." : "Waiting for the speaker…"}
        />
        <div className="flex items-center gap-2 border-t border-line p-3">
          <button type="button" className="btn" onClick={() => setShowSource((v) => !v)} aria-pressed={showSource}>
            {showSource ? <EyeOff size={16} /> : <Eye size={16} />} Original
          </button>
          <div className="ms-auto flex items-center gap-1">
            <button type="button" className="btn h-11 w-11 px-0" onClick={() => setScale((s) => Math.max(0.85, s - 0.1))} aria-label="Smaller text">
              <Minus size={16} />
            </button>
            <button type="button" className="btn h-11 w-11 px-0" onClick={() => setScale((s) => Math.min(2, s + 0.1))} aria-label="Larger text">
              <Plus size={16} />
            </button>
          </div>
        </div>
      </main>
      <p className="text-center text-xs text-faint">This page never uses your microphone.</p>
    </div>
  );
}

function minutes(seconds: number): string {
  return seconds < 60 ? `${Math.max(1, Math.round(seconds))} s` : `${Math.round(seconds / 60)} min`;
}

function DownloadCard({
  code,
  lang,
  recordings,
  expiresInS,
  name,
}: {
  code: string;
  lang: string | null;
  recordings: Record<string, RecordingInfo>;
  expiresInS: number | null;
  name: (code: string) => string;
}) {
  // The listener's own language first, then any other recording of this session.
  const langs = Object.keys(recordings).sort((a, b) => (a === lang ? -1 : b === lang ? 1 : a.localeCompare(b)));
  const [first, ...others] = langs;
  if (!first) return null;
  const info = recordings[first]!;
  return (
    <section className="card flex flex-col gap-3 border-accent p-4" aria-label="Download the translation audio">
      <p className="text-sm">The session has ended. Keep the translation to listen again later.</p>
      <a
        className="btn min-h-12 self-start border-accent bg-accent px-5 text-base font-medium text-accent-ink"
        href={recordingUrl(code, first)}
        download={`interligne-${code}-${first}.mp3`}
      >
        <Download size={18} /> Download {name(first)} audio (MP3, {minutes(info.seconds)})
      </a>
      {others.length ? (
        <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted">
          Also available:
          {others.map((l) => (
            <a key={l} className="underline" href={recordingUrl(code, l)} download={`interligne-${code}-${l}.mp3`}>
              {name(l)} ({minutes(recordings[l]!.seconds)})
            </a>
          ))}
        </p>
      ) : null}
      {expiresInS != null ? (
        <p className="text-xs text-faint">Available for about {Math.max(1, Math.round(expiresInS / 60))} more minutes, then deleted.</p>
      ) : null}
    </section>
  );
}
